import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "@/components/common";
import { useToast } from "@/components/common/Toast";
import { jobsService } from "@/api/services/jobs.service";
import { sitesService } from "@/api/services/sites.service";
import { documentsService, } from "@/api/services/documents.service";
import { getAdditionalInformationFromNotes, mergeAdditionalInformationIntoNotes, } from "@/utils/buildingPermitNotes";
import { jobToUpdateDto } from "@/utils/jobUtils";
import { formatDateOnlyDisplay, nowAUSISO } from "@/utils/dateTime";
import { getBuildingPermitSlotsFromSiteDocuments } from "@/utils/buildingPermitDocuments";
import styles from "./ViewJobModal.module.css";
const CalendarIcon = () => (_jsxs("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("rect", { x: "3", y: "4", width: "18", height: "18", rx: "2", ry: "2" }), _jsx("line", { x1: "16", y1: "2", x2: "16", y2: "6" }), _jsx("line", { x1: "8", y1: "2", x2: "8", y2: "6" }), _jsx("line", { x1: "3", y1: "10", x2: "21", y2: "10" })] }));
const FileIcon = () => (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" }), _jsx("polyline", { points: "13 2 13 9 20 9" })] }));
const DownloadIcon = () => (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" }), _jsx("polyline", { points: "7 10 12 15 17 10" }), _jsx("line", { x1: "12", y1: "15", x2: "12", y2: "3" })] }));
const UploadIcon = () => (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" }), _jsx("polyline", { points: "17 8 12 3 7 8" }), _jsx("line", { x1: "12", y1: "3", x2: "12", y2: "15" })] }));
const formatDate = (dateString) => dateString ? formatDateOnlyDisplay(dateString) : "TBD";
const formatTime = (timeString) => {
    const s = (timeString ?? "").trim();
    return s || "TBD";
};
export const ViewJobModal = ({ isOpen, onClose, job, onRefresh, fullJob, }) => {
    const navigate = useNavigate();
    const toast = useToast();
    const buildingPermitInputRef = useRef(null);
    const cfiInputRef = useRef(null);
    const [uploadingBuildingPermit, setUploadingBuildingPermit] = useState(false);
    const [uploadingCfi, setUploadingCfi] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [jobDocs, setJobDocs] = useState({
        site_map: null,
        notice_to_vacate: null,
        certificates: null,
        other: null,
        building_permit: null,
        cfi_certificate: null,
    });
    const [jobDocsLoading, setJobDocsLoading] = useState(false);
    const [siteDocuments, setSiteDocuments] = useState([]);
    const [siteDocsLoading, setSiteDocsLoading] = useState(false);
    const [tenancyData, setTenancyData] = useState(null);
    const [additionalInfoValue, setAdditionalInfoValue] = useState("");
    const [savingAdditionalInfo, setSavingAdditionalInfo] = useState(false);
    const fetchFullJobForNotes = useCallback(async (signal) => {
        if (!job?.id)
            return;
        try {
            const response = await jobsService.getJob(job.id);
            if (signal.cancelled)
                return;
            const notes = response.data?.notes;
            const source = getAdditionalInformationFromNotes(notes) ?? "";
            setAdditionalInfoValue(source ?? "");
        }
        catch {
            if (signal.cancelled)
                return;
            const source = getAdditionalInformationFromNotes(fullJob?.notes) ?? "";
            setAdditionalInfoValue(source ?? "");
        }
    }, [job?.id, fullJob?.notes]);
    const fetchJobDocuments = useCallback(async () => {
        if (!job?.id) {
            setJobDocs({
                site_map: null,
                notice_to_vacate: null,
                certificates: null,
                other: null,
                building_permit: null,
                cfi_certificate: null,
            });
            setJobDocsLoading(false);
            return;
        }
        setJobDocsLoading(true);
        try {
            const response = await jobsService.getJobDocuments(job.id);
            const allDocs = response.data ?? [];
            const mapped = {
                site_map: null,
                notice_to_vacate: null,
                certificates: null,
                other: null,
                building_permit: null,
                cfi_certificate: null,
            };
            allDocs.forEach((doc) => {
                switch (doc.documentType) {
                    case "site_map":
                        mapped.site_map = doc;
                        break;
                    case "notice_to_vacate":
                        mapped.notice_to_vacate = doc;
                        break;
                    case "cfi_certificate":
                        mapped.cfi_certificate = doc;
                        break;
                    case "other":
                        mapped.other = doc;
                        break;
                    case "building_permit":
                        mapped.building_permit = doc;
                        break;
                    default:
                        if (doc.documentType === "certificates") {
                            mapped.certificates = doc;
                        }
                        break;
                }
            });
            setJobDocs(mapped);
        }
        catch (err) {
            console.error("Failed to fetch job documents:", err);
        }
        finally {
            setJobDocsLoading(false);
        }
    }, [job?.id]);
    const fetchSiteDocuments = useCallback(async () => {
        if (!job?.siteId) {
            setSiteDocuments([]);
            setSiteDocsLoading(false);
            return;
        }
        setSiteDocsLoading(true);
        try {
            const response = await documentsService.getDocumentsBySite(job.siteId);
            setSiteDocuments(response.data ?? []);
        }
        catch (err) {
            console.error("Failed to fetch site documents:", err);
            setSiteDocuments([]);
        }
        finally {
            setSiteDocsLoading(false);
        }
    }, [job?.siteId]);
    const fetchSiteForTenancy = useCallback(async () => {
        if (!job?.siteId) {
            setTenancyData(null);
            return;
        }
        try {
            const response = await sitesService.getSite(job.siteId);
            const site = response.data;
            if (site) {
                setTenancyData({
                    tenancyNumber: site.tenancyNumber ?? "",
                    tenancyName: site.tenancyName ?? "",
                    shoppingCentre: site.shoppingCenter ?? site.contactPerson?.shoppingCenter ?? "",
                });
            }
            else {
                setTenancyData(null);
            }
        }
        catch {
            setTenancyData(null);
        }
    }, [job?.siteId]);
    useEffect(() => {
        if (isOpen && job?.id) {
            fetchJobDocuments();
        }
    }, [isOpen, job?.id, fetchJobDocuments]);
    useEffect(() => {
        if (isOpen && job?.siteId) {
            fetchSiteDocuments();
        }
        else if (!job?.siteId) {
            setSiteDocuments([]);
            setSiteDocsLoading(false);
        }
    }, [isOpen, job?.siteId, fetchSiteDocuments]);
    useEffect(() => {
        if (isOpen && job?.siteId) {
            fetchSiteForTenancy();
        }
        else {
            setTenancyData(null);
        }
    }, [isOpen, job?.siteId, fetchSiteForTenancy]);
    useEffect(() => {
        if (!isOpen || !job?.id)
            return;
        const fromFullJob = getAdditionalInformationFromNotes(fullJob?.notes) ?? "";
        setAdditionalInfoValue(fromFullJob ?? "");
        const signal = { cancelled: false };
        fetchFullJobForNotes(signal);
        return () => {
            signal.cancelled = true;
        };
    }, [isOpen, job?.id, fullJob?.notes, fetchFullJobForNotes]);
    const handleAdditionalInfoBlur = useCallback(async () => {
        if (!job?.id || savingAdditionalInfo)
            return;
        const baseJob = fullJob ?? (await jobsService.getJob(job.id)).data;
        if (!baseJob)
            return;
        const currentStored = getAdditionalInformationFromNotes(baseJob.notes) ?? "";
        if (additionalInfoValue.trim() === currentStored)
            return;
        setSavingAdditionalInfo(true);
        try {
            const newNotes = mergeAdditionalInformationIntoNotes(baseJob.notes, additionalInfoValue);
            const dto = jobToUpdateDto(baseJob, { notes: newNotes });
            await jobsService.updateJob(job.id, dto);
            toast.success("Request for additional information saved");
            onRefresh?.();
        }
        catch (err) {
            toast.error("Failed to save");
        }
        finally {
            setSavingAdditionalInfo(false);
        }
    }, [
        job?.id,
        fullJob,
        additionalInfoValue,
        savingAdditionalInfo,
        onRefresh,
        toast,
    ]);
    const buildingPermitSlots = useMemo(() => getBuildingPermitSlotsFromSiteDocuments(siteDocuments), [siteDocuments]);
    /** For View Documents: prefer site doc (admin-selected) per slot, else job doc. */
    const viewDocumentsBySlot = useMemo(() => {
        const slotKeys = [
            "site_map",
            "notice_to_vacate",
            "certificates",
            "other",
        ];
        const result = {
            site_map: null,
            notice_to_vacate: null,
            certificates: null,
            other: null,
        };
        slotKeys.forEach((slot) => {
            const siteDocs = buildingPermitSlots[slot] ?? [];
            const siteDoc = siteDocs[0] ?? null;
            const jobDoc = jobDocs[slot];
            if (siteDoc) {
                result[slot] = { source: "site", doc: siteDoc };
            }
            else if (jobDoc) {
                result[slot] = { source: "job", doc: jobDoc };
            }
        });
        return result;
    }, [buildingPermitSlots, jobDocs]);
    const jobDescription = job?.jobDescription ?? "";
    const displayTenancy = useMemo(() => {
        const fromJob = {
            tenancyNumber: job?.tenancyNumber ?? "",
            tenancyName: job?.tenancyName ?? "",
            shoppingCentre: job?.shoppingCentre ?? "",
        };
        const fromFetch = tenancyData;
        return fromJob.tenancyNumber || fromJob.tenancyName || fromJob.shoppingCentre
            ? fromJob
            : fromFetch;
    }, [
        job?.tenancyNumber,
        job?.tenancyName,
        job?.shoppingCentre,
        tenancyData,
    ]);
    const handleDownloadJobDoc = async (doc) => {
        try {
            const response = await jobsService.getJobDocumentDownloadUrl(doc.id);
            if (response.data?.url) {
                window.open(response.data.url, "_blank");
            }
        }
        catch (err) {
            console.error("Failed to get download URL:", err);
        }
    };
    const handleDownloadSiteDoc = async (doc) => {
        try {
            const response = await documentsService.getDownloadUrl(doc.id);
            if (response.data?.url) {
                window.open(response.data.url, "_blank");
            }
        }
        catch (err) {
            console.error("Failed to get download URL:", err);
        }
    };
    const handleDownloadViewDoc = (item) => {
        if (item.source === "job") {
            handleDownloadJobDoc(item.doc);
        }
        else {
            handleDownloadSiteDoc(item.doc);
        }
    };
    const handleFileUploadClick = (category) => {
        setUploadError(null);
        if (category === "building_permit") {
            buildingPermitInputRef.current?.click();
        }
        else {
            cfiInputRef.current?.click();
        }
    };
    const handleBuildingPermitFileChange = useCallback(async (e) => {
        const file = e.target.files?.[0];
        if (!file || !job?.id)
            return;
        setUploadingBuildingPermit(true);
        setUploadError(null);
        try {
            await jobsService.uploadBuildingPermit(job.id, file);
            const jobForUpdate = fullJob ?? (await jobsService.getJob(job.id)).data;
            if (jobForUpdate) {
                const dto = jobToUpdateDto(jobForUpdate, {
                    buildingPermitApproved: true,
                    buildingPermitApprovedDate: nowAUSISO(),
                    surveyorStatus: "APPROVED",
                });
                await jobsService.updateJob(job.id, dto);
            }
            toast.success("Building permit successfully uploaded", 3000);
            onRefresh?.();
            await fetchJobDocuments();
        }
        catch (err) {
            setUploadError(err instanceof Error
                ? err.message
                : "Failed to upload building permit");
        }
        finally {
            setUploadingBuildingPermit(false);
            e.target.value = "";
        }
    }, [job?.id, fullJob, onRefresh, fetchJobDocuments, toast]);
    const handleCfiFileChange = useCallback(async (e) => {
        const file = e.target.files?.[0];
        if (!file || !job?.id)
            return;
        if (!jobDocs.building_permit) {
            toast.error("Please upload the Building Permit first");
            e.target.value = "";
            return;
        }
        setUploadingCfi(true);
        setUploadError(null);
        try {
            await jobsService.uploadCFI(job.id, file);
            const jobForUpdate = fullJob ?? (await jobsService.getJob(job.id)).data;
            if (jobForUpdate) {
                const updates = {
                    cfiApproved: true,
                    cfiApprovedDate: nowAUSISO(),
                };
                if (jobDocs.building_permit) {
                    updates.buildingPermitApproved = true;
                    updates.buildingPermitRequested = true;
                }
                const dto = jobToUpdateDto(jobForUpdate, updates);
                await jobsService.updateJob(job.id, dto);
            }
            toast.success("Certificate of Final Inspection successfully uploaded", 3000);
            onRefresh?.();
            await fetchJobDocuments();
        }
        catch (err) {
            let errorMessage = "Failed to upload Certificate of Final Inspection.";
            if (err && typeof err === "object" && "response" in err) {
                const axiosError = err;
                if (axiosError.response?.data?.message) {
                    errorMessage = axiosError.response.data.message;
                }
            }
            toast.error(errorMessage, 5000);
            setUploadError(errorMessage);
        }
        finally {
            setUploadingCfi(false);
            e.target.value = "";
        }
    }, [
        job?.id,
        jobDocs.building_permit,
        fullJob,
        onRefresh,
        fetchJobDocuments,
        toast,
    ]);
    const renderDocumentRow = (label, item, slot) => {
        const name = item ? item.doc.name || item.doc.fileName : null;
        return (_jsxs("div", { className: styles.documentRow, children: [_jsx("span", { className: styles.documentLabel, children: label }), _jsx("div", { className: styles.documentActions, children: item && name ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: styles.documentFile, children: [_jsx(FileIcon, {}), _jsx("span", { children: name })] }), _jsx("button", { className: styles.downloadBtn, onClick: () => handleDownloadViewDoc(item), type: "button", children: _jsx(DownloadIcon, {}) })] })) : (_jsx("span", { className: styles.documentEmpty, children: "No document" })) })] }, slot));
    };
    if (!job)
        return null;
    const handleViewForm = () => {
        navigate(`/surveyor/jobs/${job.id}/form`);
    };
    const getStatusBadge = () => {
        const status = job.buildingPermitStatus || "none";
        switch (status) {
            case "requested":
                return (_jsx("span", { className: styles.statusBadge, children: "Building Permit Requested" }));
            case "uploaded":
                return (_jsx("span", { className: styles.statusBadge, children: "Building Permit Uploaded" }));
            case "cfi_requested":
                return _jsx("span", { className: styles.statusBadge, children: "CFI Requested" });
            default:
                return null;
        }
    };
    return (_jsx(Modal, { isOpen: isOpen, onClose: onClose, children: _jsxs("div", { className: styles.modalContent, children: [_jsx("div", { className: styles.header, children: _jsxs("div", { className: styles.headerTop, children: [_jsxs("div", { children: [_jsxs("h2", { className: styles.jobNumber, children: ["Job #", job.jobNumber] }), job.title ? (_jsxs("p", { className: styles.location, children: ["Job Name: ", job.title] })) : null, _jsxs("p", { className: styles.location, children: ["Site: ", job.location] }), (displayTenancy?.tenancyNumber ||
                                        displayTenancy?.tenancyName ||
                                        displayTenancy?.shoppingCentre) && (_jsxs(_Fragment, { children: [displayTenancy.tenancyNumber ? (_jsxs("p", { className: styles.location, children: ["Tenancy Number: ", displayTenancy.tenancyNumber] })) : null, displayTenancy.tenancyName ? (_jsxs("p", { className: styles.location, children: ["Tenancy Name: ", displayTenancy.tenancyName] })) : null, displayTenancy.shoppingCentre ? (_jsxs("p", { className: styles.location, children: ["Shopping Centre: ", displayTenancy.shoppingCentre] })) : null] }))] }), _jsxs("div", { className: styles.headerActions, children: [getStatusBadge(), _jsxs("button", { className: styles.viewFormBtn, onClick: handleViewForm, children: [_jsx(DownloadIcon, {}), "View Form"] })] })] }) }), _jsxs("div", { className: styles.content, children: [_jsx("div", { className: styles.leftColumn, children: _jsxs("div", { className: styles.section, children: [_jsx("label", { className: styles.label, children: "Job Scope" }), _jsx("textarea", { className: styles.textarea, value: job.jobScope ?? "", placeholder: "No scope provided", readOnly: true, rows: 6 })] }) }), _jsxs("div", { className: styles.rightColumn, children: [_jsxs("div", { className: styles.dateSection, children: [_jsxs("div", { className: styles.dateItem, children: [_jsx(CalendarIcon, {}), _jsxs("div", { children: [_jsx("span", { className: styles.dateLabel, children: "Start Date:" }), _jsxs("span", { className: styles.dateValue, children: [formatDate(job.commencementDate ?? job.scheduledDate), " | ", formatTime(job.startTime)] })] })] }), _jsxs("div", { className: styles.dateItem, children: [_jsx(CalendarIcon, {}), _jsxs("div", { children: [_jsx("span", { className: styles.dateLabel, children: "End Date:" }), _jsxs("span", { className: styles.dateValue, children: [formatDate(job.completionDate ??
                                                                    job.commencementDate ??
                                                                    job.scheduledDate), " ", "| ", formatTime(job.endTime)] })] })] })] }), _jsxs("div", { className: styles.section, children: [_jsx("label", { className: styles.label, children: "Job Description" }), _jsx("textarea", { className: styles.textarea, value: jobDescription, readOnly: true, rows: 4 })] })] }), _jsxs("div", { className: styles.section, children: [_jsx("h3", { className: styles.sectionTitle, children: "View Documents" }), jobDocsLoading || (job?.siteId && siteDocsLoading) ? (_jsx("p", { className: styles.documentLoading, children: "Loading documents..." })) : (_jsxs(_Fragment, { children: [renderDocumentRow("Site Map", viewDocumentsBySlot.site_map, "site_map"), renderDocumentRow("Notice to Vacate (SOW)", viewDocumentsBySlot.notice_to_vacate, "notice_to_vacate"), renderDocumentRow("Certificate", viewDocumentsBySlot.certificates, "certificates"), renderDocumentRow("Other", viewDocumentsBySlot.other, "other")] }))] }), _jsxs("div", { className: styles.section, children: [_jsx("label", { className: styles.label, children: "Request for Additional Information" }), _jsx("textarea", { className: styles.textarea, value: additionalInfoValue, onChange: (e) => setAdditionalInfoValue(e.target.value), onBlur: handleAdditionalInfoBlur, placeholder: "Enter your request for additional information...", disabled: savingAdditionalInfo, rows: 4 })] }), _jsxs("div", { className: styles.section, children: [_jsx("h3", { className: styles.sectionTitleOrange, children: "Attach Documents" }), _jsx("input", { ref: buildingPermitInputRef, type: "file", accept: ".pdf,.doc,.docx,image/*", "aria-hidden": true, tabIndex: -1, style: { position: "absolute", width: 0, height: 0, opacity: 0 }, onChange: handleBuildingPermitFileChange }), _jsx("input", { ref: cfiInputRef, type: "file", accept: ".pdf,.doc,.docx,image/*", "aria-hidden": true, tabIndex: -1, style: { position: "absolute", width: 0, height: 0, opacity: 0 }, onChange: handleCfiFileChange }), uploadError && (_jsx("p", { className: styles.documentLoading, style: { color: "var(--color-rust)" }, children: uploadError })), _jsxs("div", { className: styles.documentRow, children: [_jsx("span", { className: styles.documentLabel, children: "Building Permit" }), _jsx("div", { className: styles.documentActions, children: jobDocs.building_permit ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: styles.documentFile, children: [_jsx(FileIcon, {}), _jsx("span", { children: jobDocs.building_permit.name ||
                                                                    jobDocs.building_permit.fileName })] }), _jsx("button", { className: styles.downloadBtn, onClick: () => handleDownloadJobDoc(jobDocs.building_permit), type: "button", children: _jsx(DownloadIcon, {}) }), _jsx("span", { className: styles.doneBadge, children: "DONE" })] })) : (_jsxs(_Fragment, { children: [_jsxs("button", { className: styles.browseBtn, type: "button", onClick: () => handleFileUploadClick("building_permit"), disabled: uploadingBuildingPermit, children: [_jsx(UploadIcon, {}), uploadingBuildingPermit ? "UPLOADING..." : "UPLOAD"] }), _jsx("span", { className: styles.dueBadge, children: "DUE" })] })) })] }), _jsxs("div", { className: styles.documentRow, children: [_jsxs("span", { className: styles.documentLabel, children: ["Certificate of Final Inspection", !jobDocs.building_permit && !jobDocs.cfi_certificate ? (_jsxs("span", { className: styles.documentHint, children: [" ", "(Upload Building Permit first)"] })) : null] }), _jsx("div", { className: styles.documentActions, children: jobDocs.cfi_certificate ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: styles.documentFile, children: [_jsx(FileIcon, {}), _jsx("span", { children: jobDocs.cfi_certificate.name ||
                                                                    jobDocs.cfi_certificate.fileName })] }), _jsx("button", { className: styles.downloadBtn, onClick: () => handleDownloadJobDoc(jobDocs.cfi_certificate), type: "button", children: _jsx(DownloadIcon, {}) }), _jsx("span", { className: styles.doneBadge, children: "DONE" })] })) : (_jsxs(_Fragment, { children: [_jsxs("button", { className: styles.browseBtn, type: "button", onClick: () => handleFileUploadClick("cfi"), disabled: uploadingCfi || !jobDocs.building_permit, children: [_jsx(UploadIcon, {}), uploadingCfi ? "UPLOADING..." : "UPLOAD"] }), (fullJob?.cfiRequested ?? job.buildingPermitStatus === "cfi_requested") && (_jsx("span", { className: styles.dueBadge, children: "DUE" }))] })) })] })] })] }), _jsx("div", { className: styles.footer, children: _jsx("button", { className: styles.closeBtn, type: "button", onClick: onClose, children: "CLOSE" }) })] }) }));
};
